import { useCallback } from 'react';
import toast from 'react-hot-toast';
import type { ClipboardEvent } from 'react';

interface UseClipboardFilePasteOptions {
  /**
   * Callback fired with the extracted files from the clipboard.
   * You can return a promise to handle async workflows (e.g. uploads).
   */
  onFiles: (files: File[]) => void | Promise<void>;

  /**
   * Optional label used when generating fallback filenames and error messages.
   */
  contextLabel?: string;
}

const buildFallbackName = (contextLabel: string | undefined, index: number, file: File) => {
  const timestamp = Date.now();
  const sanitizedContext = contextLabel
    ? contextLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-')
    : 'pasted-file';
  const extension =
    file.type && file.type.includes('/') ? file.type.split('/')[1] : 'png';

  return `${sanitizedContext}-${timestamp}-${index}.${extension}`;
};

/**
 * Provides a paste handler that extracts `File` objects from the clipboard
 * (e.g. screenshots copied via PrintScreen) and forwards them to the supplied callback.
 * Text content is ignored, allowing the default paste behaviour for non-file clipboard data.
 */
export const useClipboardFilePaste = ({
  onFiles,
  contextLabel,
}: UseClipboardFilePasteOptions) => {
  return useCallback(
    (event: ClipboardEvent<HTMLElement>) => {
      const clipboardItems = event.clipboardData?.items;
      if (!clipboardItems || clipboardItems.length === 0) {
        return;
      }

      const files: File[] = [];

      Array.from(clipboardItems).forEach((item, index) => {
        if (item.kind !== 'file') return;
        const file = item.getAsFile();
        if (!file) return;

        if (file.name && file.name.trim().length > 0) {
          files.push(file);
          return;
        }

        const fallbackName = buildFallbackName(contextLabel, index, file);
        try {
          const cloned = new File([file], fallbackName, { type: file.type || 'application/octet-stream' });
          files.push(cloned);
        } catch (error) {
          try {
            Object.defineProperty(file, 'name', {
              value: fallbackName,
              configurable: true,
            });
          } catch {
            console.warn('Unable to set fallback filename for clipboard file', error);
          }
          files.push(file);
        }
      });

      if (!files.length) {
        return;
      }

      event.preventDefault();

      Promise.resolve(onFiles(files)).catch((error) => {
        console.error('Failed to handle pasted files', error);
        toast.error('Failed to process pasted files. Please try again.');
      });
    },
    [contextLabel, onFiles],
  );
};


